import React, { useState, useEffect, useCallback, useRef } from "react";
import { PAGE_SIZE } from "../../../api";
import { fetchTicketStats, fetchTickets as fetchTicketsApi } from "../services/TicketsService";
import { StatusBadge, PriorityBadge } from "../../../components/Badge";
import DataTable from "../../../components/DataTable";
import SearchInput from "../../../components/SearchInput";
import FilterSelect from "../../../components/FilterSelect";
import { KpiGrid, KpiCard } from "../../../components/KpiCard";
import TicketDetailModal from "../../../components/TicketDetailModal";

export default function TicketsView() {
  const [tickets, setTickets] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [page, setPage] = useState(1);
  const [hasTickets, setHasTickets] = useState(true);
  const hasLoadedTicketsRef = useRef(false);
  const ticketsRequestIdRef = useRef(0);
  
  const [filters, setFilters] = useState({
    search: "",
    status: "open",
    priority: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    status: "open",
    priority: "",
  });

  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    in_progress: 0,
    resolved: 0,
    critical: 0,
  });

  const fetchStats = useCallback(async () => {
    try {
      const data = await fetchTicketStats();
      if (data) {
        setStats(data);
      }
    } catch (e) {
      console.error("Could not load ticket stats:", e);
    }
  }, []);

  const fetchTickets = useCallback(async ({ background = false } = {}) => {
    const requestId = ++ticketsRequestIdRef.current;
    const isBackgroundRefresh = background && hasLoadedTicketsRef.current;

    if (isBackgroundRefresh) {
      setRefreshing(true);
      setRefreshError(null);
    } else {
      setLoading(true);
      setRefreshing(false);
      setError(null);
      setRefreshError(null);
    }

    try {
      const offset = (page - 1) * PAGE_SIZE;
      const data = await fetchTicketsApi({
        limit: PAGE_SIZE,
        offset: offset,
        search: appliedFilters.search,
        status: appliedFilters.status,
        priority: appliedFilters.priority,
      });
      if (requestId !== ticketsRequestIdRef.current) return;

      const items = data?.items || [];
      const total = data?.total || 0;
      setTickets(items);
      setTotalCount(total);
      setError(null);
      setRefreshError(null);
      hasLoadedTicketsRef.current = true;
      if (!appliedFilters.search && !appliedFilters.status && !appliedFilters.priority) {
        setHasTickets(total > 0 || items.length > 0);
      }
    } catch {
      if (requestId !== ticketsRequestIdRef.current) return;
      if (isBackgroundRefresh) {
        setRefreshError("Could not refresh tickets. Showing the last loaded data.");
      } else {
        setError("Could not load tickets.");
      }
    } finally {
      if (requestId === ticketsRequestIdRef.current) {
        if (isBackgroundRefresh) setRefreshing(false);
        else setLoading(false);
      }
    }
  }, [page, appliedFilters]);

  // Sync tickets with page/filters
  useEffect(() => {
    fetchTickets();
  }, [page, appliedFilters, fetchTickets]);

  // Poll ticket data without replacing the visible rows with loading skeletons.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTickets({ background: true });
      fetchStats();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchTickets, fetchStats]);

  // Fetch stats on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Debounce search filter input changes for premium instant-feel filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      if (appliedFilters.search !== filters.search) {
        setAppliedFilters((prev) => ({ ...prev, search: filters.search }));
        setPage(1);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [filters.search, appliedFilters.search]);

  const handleFilterChange = (key, val) => {
    setFilters((prev) => ({ ...prev, [key]: val }));
    if (key !== "search") {
      setAppliedFilters((prev) => ({ ...prev, [key]: val }));
      setPage(1);
    }
  };

  function clearFilters() {
    const empty = { search: "", status: "", priority: "" };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  }

  const hasActiveFilter =
    appliedFilters.search || appliedFilters.status || appliedFilters.priority;
  const hasActiveFilters = hasActiveFilter;

  let activeCardLabel = "";
  if (appliedFilters.status === "open" && !appliedFilters.priority) {
    activeCardLabel = "Open";
  } else if (appliedFilters.status === "in_progress" && !appliedFilters.priority) {
    activeCardLabel = "In Progress";
  } else if (appliedFilters.status === "resolved" && !appliedFilters.priority) {
    activeCardLabel = "Resolved";
  } else if (!appliedFilters.status && !appliedFilters.priority) {
    activeCardLabel = "Total";
  }

  const handleCardClick = (label) => {
    const isActive = activeCardLabel === label;
    if (isActive || label === "Total") {
      setFilters((prev) => ({ ...prev, status: "", priority: "" }));
      setAppliedFilters((prev) => ({ ...prev, status: "", priority: "" }));
    } else if (label === "Open") {
      setFilters((prev) => ({ ...prev, status: "open", priority: "" }));
      setAppliedFilters((prev) => ({ ...prev, status: "open", priority: "" }));
    } else if (label === "In Progress") {
      setFilters((prev) => ({ ...prev, status: "in_progress", priority: "" }));
      setAppliedFilters((prev) => ({ ...prev, status: "in_progress", priority: "" }));
    } else if (label === "Resolved") {
      setFilters((prev) => ({ ...prev, status: "resolved", priority: "" }));
      setAppliedFilters((prev) => ({ ...prev, status: "resolved", priority: "" }));
    }
    setPage(1);
  };

  const statCards = [
    {
      label: "Total",
      value: stats.total,
      color: "#1D4ED8",
      bg: "#EFF6FF",
      icon: "ticket",
    },
    {
      label: "Open",
      value: stats.open,
      color: "#2563EB",
      bg: "#DBEAFE",
      icon: "info",
    },
    {
      label: "In Progress",
      value: stats.in_progress,
      color: "#C2410C",
      bg: "#FFF7ED",
      icon: "clock",
    },
    {
      label: "Resolved",
      value: stats.resolved,
      color: "#059669",
      bg: "#ECFDF5",
      icon: "check",
    },
  ];

  const ticketColumns = [
    {
      header: "ID",
      tip: "Ticket identifier",
      render: (_, ticket) => (
        <span
          style={{
            fontFamily: "monospace",
            background: "#F1F5F9",
            border: "1px solid #E2E8F0",
            padding: "2px 6px",
            borderRadius: 4,
            fontSize: 11,
            color: "#475569",
            whiteSpace: "nowrap",
          }}
        >
          {ticket.id}
        </span>
      ),
    },
    {
      header: "Title & Description",
      tip: "Ticket title and short description",
      style: { maxWidth: 300 },
      render: (_, ticket) => (
        <>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#0F172A",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {ticket.title}
          </div>
          {ticket.ticket_description && (
            <div
              style={{
                fontSize: 11,
                color: "#64748B",
                marginTop: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {ticket.ticket_description}
            </div>
          )}
        </>
      ),
    },
    {
      header: "Status",
      tip: "Current workflow status",
      render: (_, ticket) => <StatusBadge status={ticket.status} />,
    },
    {
      header: "Priority",
      tip: "Urgency level",
      render: (_, ticket) => <PriorityBadge priority={ticket.priority} />,
    },
    {
      header: "Source",
      tip: "How this ticket was created",
      render: (_, ticket) => (
        <span style={{ fontSize: 11.5, color: "#475569", fontWeight: 500 }}>
          {ticket.source}
        </span>
      ),
    },
    {
      header: "Created",
      tip: "Date ticket was opened",
      render: (_, ticket) => (
        <span style={{ fontSize: 11.5, color: "#64748B", whiteSpace: "nowrap" }}>
          {ticket.created_at
            ? new Date(ticket.created_at).toLocaleString("en-IN")
            : "—"}
        </span>
      ),
    },
  ];

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F8FAFC",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "18px 24px 14px",
          borderBottom: "1px solid #E5E7EB",
          background: "#fff",
        }}
      >
        <div style={{ marginBottom: 14 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "#111827",
            }}
          >
            Tickets
          </h1>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6B7280" }}>
            Track and resolve operational issues
          </p>
        </div>
        {/* Stat cards */}
        <KpiGrid>
          {statCards.map((s) => (
            <KpiCard
              key={s.label}
              icon={s.icon}
              label={s.label}
              value={s.value}
              color={s.color}
              bg={s.bg}
              active={activeCardLabel === s.label}
              onClick={() => handleCardClick(s.label)}
            />
          ))}
        </KpiGrid>
        
        {(refreshing || refreshError) && (
          <div
            role="status"
            aria-live="polite"
            style={{
              minHeight: 18,
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              color: refreshError ? "#B91C1C" : "#64748B",
            }}
          >
            {refreshing && <span>Refreshing tickets...</span>}
            {refreshError && <span>{refreshError}</span>}
          </div>
        )}

        {/* Filters */}
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginTop: 14,
          }}
        >
          <SearchInput
            placeholder="Search tickets by title, description or source…"
            value={filters.search}
            onChange={(val) => handleFilterChange("search", val)}
            style={{ flex: 1, minWidth: 200 }}
          />

          <FilterSelect
            value={filters.status}
            onChange={(val) => handleFilterChange("status", val)}
            placeholder="All statuses"
            options={[
              { value: "open", label: "Open" },
              { value: "in_progress", label: "In Progress" },
              { value: "resolved", label: "Resolved" },
              { value: "closed", label: "Closed" },
            ]}
          />

          <FilterSelect
            value={filters.priority}
            onChange={(val) => handleFilterChange("priority", val)}
            placeholder="All priorities"
            options={[
              { value: "p1", label: "P1 - Critical" },
              { value: "p2", label: "P2 - High" },
              { value: "p3", label: "P3 - Medium" },
              { value: "p4", label: "P4 - Low" },
            ]}
          />
        </div>

        {hasActiveFilters && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 10,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 11, color: "#6B7280" }}>
              Active filters:
            </span>
            {appliedFilters.status && (
              <span
                style={{
                  fontSize: 11,
                  background: "#F3F4F6",
                  color: "#374151",
                  padding: "2px 9px",
                  borderRadius: 10,
                  fontWeight: 500,
                }}
              >
                Status: {appliedFilters.status.replace("_", " ")}
              </span>
            )}
            {appliedFilters.priority && (
              <span
                style={{
                  fontSize: 11,
                  background: "#F3F4F6",
                  color: "#374151",
                  padding: "2px 9px",
                  borderRadius: 10,
                  fontWeight: 500,
                }}
              >
                Priority: {appliedFilters.priority.toUpperCase()}
              </span>
            )}
            {appliedFilters.search && (
              <span
                style={{
                  fontSize: 11,
                  background: "#F3F4F6",
                  color: "#374151",
                  padding: "2px 9px",
                  borderRadius: 10,
                  fontWeight: 500,
                }}
              >
                Search: "{appliedFilters.search}"
              </span>
            )}
            <button
              type="button"
              onClick={clearFilters}
              style={{
                background: "none",
                border: "none",
                color: "#2563EB",
                fontSize: 11,
                cursor: "pointer",
                padding: 0,
                marginLeft: 4,
              }}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        <DataTable
          columns={ticketColumns}
          data={tickets}
          loading={loading}
          error={error}
          onRetry={fetchTickets}
          emptyTitle={hasTickets ? "No results found" : "No tickets found"}
          emptySubtitle={
            hasTickets
              ? "No matching items were found for your search query. Try clearing the search or adjusting filters."
              : "No tickets are registered in the system."
          }
          emptyIcon={hasTickets ? "search" : "ticket"}
          onRowClick={(ticket) => setSelectedTicket(ticket)}
          page={page}
          total={totalCount}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          itemLabel="tickets"
        />
      </div>

      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdated={(updated, options = {}) => {
            if (options.deleted) {
              setTickets((prev) => prev.filter((t) => t.id !== updated.id));
              setSelectedTicket(null);
            } else {
              setTickets((prev) =>
                prev.map((t) => (t.id === updated.id ? updated : t))
              );
              setSelectedTicket(updated);
            }
            fetchTickets();
            fetchStats();
          }}
        />
      )}
    </div>
  );
}
